import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import PricingDialog from "@/components/PricingDialog";
import { Zap } from "lucide-react";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";

const FAQS = [
  {
    q: "Is this really real-time?",
    a: "Yes. Our platform ingests data from 100+ verified OSINT sources every 60 seconds. Events typically appear on our map within 2 minutes of occurrence — often 30–50 minutes before mainstream media reports them.",
  },
  {
    q: "How is this different from following the news?",
    a: "News outlets report selectively and with delay. We aggregate raw intelligence from military trackers, seismic sensors, flight radars, satellite imagery, and economic feeds — then our AI cross-references everything to surface patterns no single news source can see.",
  },
  {
    q: "Who uses Global Conflict Monitor?",
    a: "Security analysts, OSINT researchers, journalists, NGO field coordinators, corporate risk teams, and informed citizens in 40+ countries. Anyone who needs to know what's happening before it's on TV.",
  },
  {
    q: "What do I get with the free plan?",
    a: "The free plan gives you access to the live conflict map, basic event data, and daily briefings. Premium unlocks all 56 tools, real-time push alerts, AI analysis, market correlation, and priority data access.",
  },
  {
    q: "How much does Premium cost?",
    a: "Premium starts at €2.99/month (or €1.67/month billed annually at €19.99/year). That's less than a coffee for the most comprehensive intelligence platform available to the public.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Absolutely. No contracts, no hidden fees. Cancel with one click from your account settings. Your access continues until the end of your billing period.",
  },
  {
    q: "Is my data safe?",
    a: "We take privacy seriously. We don't sell your data, we don't track your location unless you explicitly enable alerts, and all connections are encrypted. Read our full Privacy Policy for details.",
  },
  {
    q: "How accurate is the data?",
    a: "We only surface verified events from trusted sources (ACLED, UCDP, Reuters, SIPRI, USGS, NASA, and more). Each event includes a confidence score and source attribution so you can assess reliability yourself.",
  },
];

const FAQ = () => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { formatPriceWithEur } = useLocalCurrency();

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById("faq-schema")?.remove(); };
  }, []);

  return (
    <section id="faq" className="py-8 md:py-12 border-b border-border">
      <div className="container mx-auto px-4 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center mb-6"
        >
          <h2 className="text-lg md:text-2xl font-black text-foreground">
            Frequently Asked Questions
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Everything you need to know before getting started.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="space-y-2">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-lg border border-border bg-card px-4 data-[state=open]:border-primary/30 data-[state=open]:bg-primary/5 transition-colors"
              >
                <AccordionTrigger className="text-sm font-semibold text-foreground hover:text-primary py-3 [&[data-state=open]>svg]:rotate-180">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-xs leading-relaxed text-muted-foreground pb-3">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center"
        >
          <p className="text-xs text-muted-foreground mb-3">
            Still have questions? Start exploring — it's free.
          </p>
          <button
            onClick={() => setPricingOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline transition-colors"
          >
            <Zap className="h-3 w-3" />
            Or unlock everything from {formatPriceWithEur(2.99)}/mo
          </button>
        </motion.div>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>
  );
};

export default FAQ;
